/**
 * Serviço para gerenciar produtos
 */

import { getToken } from "./tokenService";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface ClientInfo {
  id: string;
  name: string;
  email: string;
  company: string;
  status_name?: string;
}

export interface ProductStatus {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  model_id: string;
  model_name: string;
  model_identifier?: string;
  agent_id: string;
  agent_name: string;
  agent_service?: string;
  agent_type_name?: string;
  status_id: number;
  status_name: string;
  status_description?: string;
  status_is_active?: boolean;
  status: ProductStatus;
  created_at: string;
  updated_at: string;
  clients: ClientInfo[];
}

export interface ProductsListResponse {
  products: Product[];
  total: number;
  pagination: {
    limit: number;
    offset: number;
    total: number;
    pages: number;
  };
}

export interface CreateProductRequest {
  name: string;
  description?: string;
  model_id: string;
  agent_id: string;
  status_id: number;
  client_ids?: string[];
}

export interface UpdateProductRequest {
  name?: string;
  description?: string;
  model_id?: string;
  agent_id?: string;
  status_id?: number;
  client_ids?: string[];
}

export interface ProductsStats {
  total_products: number;
  products_by_status: Array<{ status_name: string; count: string }>;
  products_by_model: Array<{ model_name: string; count: string }>;
  products_by_agent: Array<{ agent_name: string; count: string }>;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  if (!token) {
    throw new Error("Token de autenticação não encontrado");
  }

  const config: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  if (body && method !== "GET") {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "HTTP_ERROR",
          message: `Erro HTTP: ${response.status}`,
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message:
          error instanceof Error ? error.message : "Erro de conexão com a API",
      },
    };
  }
}

/**
 * Lista produtos com filtros e paginação
 */
export async function listProducts(options?: {
  limit?: number;
  offset?: number;
  status_id?: number;
  model_id?: string;
  agent_id?: string;
  search?: string;
}): Promise<ApiResponse<ProductsListResponse>> {
  const params = new URLSearchParams();

  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.status_id)
    params.append("status_id", options.status_id.toString());
  if (options?.model_id) params.append("model_id", options.model_id);
  if (options?.agent_id) params.append("agent_id", options.agent_id);
  if (options?.search) params.append("search", options.search);

  const queryString = params.toString();
  return apiRequest<ProductsListResponse>(
    `/products${queryString ? `?${queryString}` : ""}`
  );
}

/**
 * Obtém um produto por ID
 */
export async function getProductById(
  id: string
): Promise<ApiResponse<Product>> {
  return apiRequest<Product>(`/products/${id}`);
}

/**
 * Cria um novo produto
 */
export async function createProduct(
  product: CreateProductRequest
): Promise<ApiResponse<Product>> {
  return apiRequest<Product>("/products", "POST", product);
}

/**
 * Atualiza um produto
 */
export async function updateProduct(
  id: string,
  product: UpdateProductRequest
): Promise<ApiResponse<Product>> {
  return apiRequest<Product>(`/products/${id}`, "PUT", product);
}

/**
 * Remove um produto
 */
export async function deleteProduct(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/products/${id}`, "DELETE");
}

/**
 * Altera o status de um produto
 */
export async function changeProductStatus(
  id: string,
  statusId: number
): Promise<ApiResponse<Product>> {
  return apiRequest<Product>(`/products/${id}/status`, "PATCH", {
    status_id: statusId,
  });
}

/**
 * Associa um cliente a um produto
 */
export async function associateClientToProduct(
  productId: string,
  clientId: string
): Promise<ApiResponse<Product>> {
  return apiRequest<Product>(`/products/${productId}/clients`, "POST", {
    client_id: clientId,
  });
}

/**
 * Remove a associação de um cliente com um produto
 */
export async function dissociateClientFromProduct(
  productId: string,
  clientId: string
): Promise<ApiResponse<void>> {
  return apiRequest<void>(
    `/products/${productId}/clients/${clientId}`,
    "DELETE"
  );
}

/**
 * Obtém estatísticas dos produtos
 */
export async function getProductsStats(): Promise<ApiResponse<ProductsStats>> {
  return apiRequest<ProductsStats>("/products/stats");
}

/**
 * Lista os tipos de status de produtos
 */
export interface ProductStatusType {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export async function listProductStatusTypes(): Promise<
  ApiResponse<ProductStatusType[]>
> {
  return apiRequest<ApiResponse<ProductStatusType[]>>("/types/product-status");
}
